import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Link, Save } from "lucide-react";
import { cn } from "@/lib/utils";

interface LinkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (url: string, text?: string) => void;
  initialUrl?: string;
  initialText?: string;
  hasSelection?: boolean;
}

export const LinkDialog = ({
  isOpen,
  onClose,
  onSave,
  initialUrl = "https://",
  initialText = "",
  hasSelection = false,
}: LinkDialogProps) => {
  const [url, setUrl] = useState(initialUrl);
  const [text, setText] = useState(initialText);

  useEffect(() => {
    if (isOpen) {
      setUrl(initialUrl || "https://");
      setText(initialText || "");
    }
  }, [isOpen, initialUrl, initialText]);

  const handleSave = () => {
    if (url.trim() && url !== "https://") {
      onSave(url.trim(), hasSelection ? undefined : text.trim());
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />

      {/* Dialog */}
      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md">
        <div className="bg-background rounded-lg shadow-xl border border-border p-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Link className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold text-foreground">
                {initialUrl && initialUrl !== "https://"
                  ? "Edit Link"
                  : "Insert Link"}
              </h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="link-url" className="text-sm font-medium">
                URL
              </Label>
              <Input
                id="link-url"
                type="url"
                placeholder="https://example.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full"
                autoFocus
              />
            </div>

            {!hasSelection && (
              <div className="space-y-2">
                <Label htmlFor="link-text" className="text-sm font-medium">
                  Display Text
                </Label>
                <Input
                  id="link-text"
                  type="text"
                  placeholder="Link text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="w-full"
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6">
            <Button
              size="sm"
              variant="outline"
              onClick={onClose}
              className="px-4"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="default"
              size="sm"
              disabled={
                !url.trim() ||
                url === "https://" ||
                (!hasSelection && !text.trim())
              }
              className="px-4 text-white"
            >
              <Save className="h-4 w-4" />
              Save Link
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
